import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { personaOpportunitySetDisplayTimestamp } from "../../../lib/persona-opportunity-set-mapper.js";
import type { PersonaOpportunitySet } from "../../../types/persona-opportunities.types.js";

export interface PersonaOpportunitySetPickerProps {
  sets: PersonaOpportunitySet[];
  selectedSet: PersonaOpportunitySet;
  onSelect: (set: PersonaOpportunitySet) => void;
  onSeeAll: () => void;
}

export function PersonaOpportunitySetPicker({
  sets,
  selectedSet,
  onSelect,
  onSeeAll,
}: PersonaOpportunitySetPickerProps) {
  const { t } = useTranslation();

  const selectableSets = sets.filter((set) => set.status !== "GENERATING");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          iconEnd="chevron-down"
          className="-ml-2 h-auto max-w-xs min-h-11 min-w-11 px-2 py-0.5 text-lg font-semibold tracking-tight text-foreground hover:bg-muted [@media(hover:hover)_and_(pointer:fine)]:min-h-0 [@media(hover:hover)_and_(pointer:fine)]:min-w-0"
          aria-label={t("personaOpportunities.workspace.switchSetAria", {
            name: selectedSet.name,
            defaultValue: `Switch set, current: ${selectedSet.name}`,
          })}
        >
          <span className="truncate">{selectedSet.name}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[300px] p-0">
        <DropdownMenuLabel className="bg-muted px-3 py-2 text-[10px] font-medium tracking-[0.06em] text-muted-foreground uppercase">
          {t("personaOpportunities.workspace.switchSet", "Switch set")}
        </DropdownMenuLabel>
        {selectableSets.map((set) => {
          const date = new Date(personaOpportunitySetDisplayTimestamp(set)).toLocaleDateString(
            undefined,
            {
              month: "short",
              day: "numeric",
              year: "numeric",
            },
          );
          const isSelected = set.id === selectedSet.id;
          return (
            <DropdownMenuItem
              key={set.id}
              onClick={() => onSelect(set)}
              className="flex cursor-pointer items-center gap-2.5 rounded-none px-3 py-2.5"
            >
              <span
                className={
                  isSelected
                    ? "size-1.5 shrink-0 rounded-full bg-primary"
                    : "size-1.5 shrink-0 rounded-full bg-muted-foreground/40"
                }
                aria-hidden
              />
              <span className="min-w-0 flex-1 truncate text-[13px]">{set.name}</span>
              <span className="shrink-0 text-[11px] text-muted-foreground">{date}</span>
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuItem
          onClick={onSeeAll}
          className="cursor-pointer rounded-none border-t border-border px-3 py-2.5 text-[13px] text-primary focus:text-primary"
        >
          {t("personaOpportunities.workspace.viewAllSets", "View all sets")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
